import { useMemo, useState, useEffect } from "react";
import { VStack, Box, Text, HStack, Heading } from "@bpba/ui-components";
import { UrlsBack, UrlsFront } from '../constants/urls';
import { useNavigate } from 'react-router-dom';
import { FaFilter } from "react-icons/fa";
import styled from 'styled-components';
import { ModuleContainerGeneric } from "@bpba/modules-components";
import { normalizarTexto, corregirTildesTexto, sentenceCase } from "../utils/ajusteDatosBackend";
import { buildAssetUrl } from '../utils/inmuebles.service';
import { Categoria } from "./beneficios/Categoria";
import { useInmuebles } from '../hooks/useInmuebles';
import { useLocalidades } from '../hooks/useLocalidades';
import { LoadingComponent } from "./shared/LoadingComponent";

interface Props {
    onInmueblesLoaded?: (ids: number[]) => void;
}

export default function ListadoInmuebles({ onInmueblesLoaded }: Props) {

    const { data: inmuebles, loading } = useInmuebles();
    const { localidades } = useLocalidades();

    const navigate = useNavigate();

    function getNombreLocalidad(codigo?: number | string) {
        if (codigo === null || codigo === undefined) {
            return "Sin localidad";
        }

        return localidades[Number(codigo)] ?? codigo.toString();
    }

    const [tiposSeleccionados, setTiposSeleccionados] = useState<Set<string>>(new Set());

    const [localidadesSeleccionadas, setLocalidadesSeleccionadas] = useState<Set<string>>(new Set())

    // Define tipos visibles
    const dataFiltradaPorLocalidad = useMemo(() => {
        if (localidadesSeleccionadas.size === 0) return inmuebles;
        return inmuebles?.filter(x => localidadesSeleccionadas.has(x.localidad));
    }, [inmuebles, localidadesSeleccionadas]);

    // Tipos visibles (según la selección de localidades)
    const tiposVisibles = useMemo(() => {
        return Array.from(
            new Set(dataFiltradaPorLocalidad?.map(x => x.descripcionDelBien))
        ).sort((a, b) =>
            a.localeCompare(
                b,
                'es',
                { sensitivity: 'base' }
            )
        );
    }, [dataFiltradaPorLocalidad]);

    // Define localidades visibles
    const dataFiltradaPorTipo = useMemo(() => {
        if (tiposSeleccionados.size === 0) return inmuebles;
        return inmuebles?.filter(x => tiposSeleccionados.has(x.descripcionDelBien));
    }, [inmuebles, tiposSeleccionados]);

    // Localidades visibles (según la selección de tipos)
    const localidadesVisibles = useMemo(() => {
        return Array.from(
            new Set(dataFiltradaPorTipo?.map(x => x.localidad))
        ).sort((a, b) => {
            const nombreA = getNombreLocalidad(a);
            const nombreB = getNombreLocalidad(b);

            return nombreA.localeCompare(
                nombreB,
                'es',
                { sensitivity: 'base' }
            );
        });
    }, [dataFiltradaPorTipo, localidades]);


    //Si el usuario tenía tildado algo que ya no está visible, se destilda para evitar estados imposibles.
    useEffect(() => {
        setTiposSeleccionados(prev => {
            if (prev.size === 0) return prev;
            const visiblesSet = new Set(tiposVisibles);

            const next = new Set<string>(
                Array.from(prev).filter(t => visiblesSet.has(t))
            );

            return next.size === prev.size ? prev : next;
        });
    }, [tiposVisibles]);

    useEffect(() => {
        setLocalidadesSeleccionadas(prev => {
            if (prev.size === 0) return prev;
            const visiblesSet = new Set(localidadesVisibles);

            const next = new Set<string>(
                Array.from(prev).filter(l => visiblesSet.has(l))
            );

            return next.size === prev.size ? prev : next;
        });
    }, [localidadesVisibles]);

    //Para mantener el scroll del usuario al volver desde el detalle, se guarda la posición antes de navegar y se vuelve a esa posición al cargar el listado.
    useEffect(() => {
        if (inmuebles?.length === 0) return;

        const scroll = sessionStorage.getItem('scrollListado');

        if (scroll) {
            setTimeout(() => {
                window.scrollTo(0, Number(scroll));
            }, 0);

            sessionStorage.removeItem('scrollListado');
        }
    }, [inmuebles]);

    // Toggle de un checkbox
    const toggle = (tipo: string) => {
        setTiposSeleccionados(prev => {
            const next = new Set(prev);
            if (next.has(tipo)) next.delete(tipo);
            else next.add(tipo);
            return next;
        });
    };

    // Toggle del checkbox
    const toggleLocalidad = (loc: string) => {
        setLocalidadesSeleccionadas(prev => {
            const next = new Set(prev);
            next.has(loc) ? next.delete(loc) : next.add(loc);
            return next;
        });
    };

    // Filtrado (si no hay selección, se muestran todos)
    const filtrados = useMemo(() => {
        return inmuebles?.filter(x => {
            const matchTipo = tiposSeleccionados.size === 0 || tiposSeleccionados.has(x.descripcionDelBien);
            const matchLoc = localidadesSeleccionadas.size === 0 || localidadesSeleccionadas.has(x.localidad);
            return matchTipo && matchLoc;
        });
    }, [inmuebles, tiposSeleccionados, localidadesSeleccionadas]);

    if (loading) {
        return <LoadingComponent />;
    }

    return (
        <ModuleContainerGeneric flexDirection={'column'} alignItems={'flex-start'} padding={'20px'} width={'100%'} paddingTop={'0px !important'}>
            <Heading lineHeight={'normal'} marginBottom={'25px !important'}>Oportunidades inmobiliarias</Heading>

            <Box flexDirection={['column', 'row', 'row', 'row']}>
                {/* Filtros */}
                <Box borderRadius={'8px 32px 8px 32px'} backgroundColor={'#dedee3'} marginRight={'25px'} marginBottom={'7px'} padding={'20px'} width={['100%', '50%', '30%', '20%']} height={'fit-content'} flexWrap={'wrap'}>
                    <HStack marginBottom={'20px'}>
                        <FaFilter color={'#279d2e'} />
                        <Text marginLeft={'5px !important'} fontWeight={'700'} color={'#279d2e'}>Filtros</Text>
                    </HStack>
                    <Box flexDirection={'column'}>
                        <Box marginBottom={'30px'} display={'flex'} height={'auto'} flexDirection={'column'}>
                            <Text fontWeight={'700'} marginBottom={'3px !important'}>Tipo de inmueble</Text>

                            {tiposVisibles.map((t) => {
                                const checked = tiposSeleccionados.has(t);

                                return (
                                    <Box key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: '5px' }}>
                                        <input
                                            type="checkbox"
                                            value={t}
                                            checked={checked}
                                            onChange={() => toggle(t)}
                                        />
                                        <Text onClick={() => toggle(t)} style={{ userSelect: 'none' }}>{corregirTildesTexto(sentenceCase(t))}</Text>
                                    </Box>
                                );
                            })}
                        </Box>
                        <Box display={'flex'} height={'auto'} flexDirection={'column'}>
                            <Text fontWeight={'700'} marginBottom={'3px !important'}>Localidad</Text>

                            {localidadesVisibles.map((loc) => {
                                const checked = localidadesSeleccionadas.has(loc);
                                return (
                                    <Box key={loc} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: '5px' }}>
                                        <input
                                            type="checkbox"
                                            value={loc}
                                            checked={checked}
                                            onChange={() => toggleLocalidad(loc)}
                                        />
                                        <Text onClick={() => toggleLocalidad(loc)} style={{ userSelect: 'none' }}>{normalizarTexto(getNombreLocalidad(loc))}</Text>
                                    </Box>
                                );
                            })}

                        </Box>
                    </Box>
                </Box>

                {/* Pantalla */}
                {filtrados?.length === 0 ?
                    <Box justifyContent={'center'} height={'100px'} alignItems={'center'}>
                        <Text fontWeight={'700'}>No hay inmuebles que coincidan con los filtros seleccionados.</Text>
                    </Box> :
                    <VStack width={'100%'}>
                        <Box display={'flex'} flexWrap={'wrap'} justifyContent={['center', 'center', 'left', 'left']} style={{ gap: '15px' }}>
                            {filtrados?.map((inm) => (
                                <VStackHover
                                    key={inm.inmueble}
                                    width={'250px'}
                                    height={'auto'}
                                    borderTopLeftRadius="8px"
                                    borderTopRightRadius="30px"
                                    borderBottomLeftRadius="32px"
                                    borderBottomRightRadius="10px"
                                    overflow={'hidden'}
                                    onClick={() => {
                                        sessionStorage.setItem('scrollListado', String(window.scrollY));
                                        navigate(UrlsFront.detalleInmueble.replace(':id', inm.inmueble.toString()));
                                    }}
                                >
                                    {/* Imagen/Carrousel */}
                                    <Box height={'250px'} display="flex" alignItems="center" justifyContent="center">
                                        {inm.imagenes && inm.imagenes.length > 0 ? (
                                            <img
                                                src={buildAssetUrl(inm.imagenes[0].clave)}
                                                width={'100%'}
                                                alt="Detalle Inmueble"
                                                style={{ objectFit: 'cover', height: '100%' }}
                                            />
                                        ) : (
                                            <Text textAlign="center" fontSize="14px" color="gray">
                                                Aún no hay imágenes para este inmueble
                                            </Text>
                                        )}
                                    </Box>

                                    <Categoria titulo={normalizarTexto(getNombreLocalidad(inm.localidad))} />
                                    <VStack p={'18px'} pb={'24px'}>
                                        <Text
                                            m={'0px'}
                                            mb="18px"
                                            fontWeight={600}
                                            fontSize="22px"
                                            lineHeight="25px">{corregirTildesTexto(normalizarTexto(inm.encabezadoWeb ? inm.encabezadoWeb : inm.estadoVenta))}
                                        </Text>
                                        <Text m={'0px'}
                                            fontSize={['16px']}
                                            lineHeight={['20px']}>{corregirTildesTexto(normalizarTexto(inm.direccion))} N° {inm.numero}
                                        </Text>
                                    </VStack>
                                </VStackHover>
                            ))}
                        </Box>
                    </VStack>
                }
            </Box>
        </ModuleContainerGeneric>
    )
}

const VStackHover = styled(VStack)({
    cursor: 'pointer',
    transition: 'box-shadow 0.3s linear',
    '-webkit-transition': 'box-shadow 0.3s linear',
    '-moz-transition': 'box-shadow 0.3s linear',
    '-ms-transition': 'box-shadow 0.3s linear',
    '-o-transition': 'box-shadow 0.3s linear',
    '&:hover': {
        boxShadow: '0px 0px 8px 2px rgba(0,0,0,0.4)',
    },
});